import {
  faExclamationTriangle,
  faMagnifyingGlass,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';

import PoolDepthCard from '../PoolDepthCard';
import WorkerRow from '../WorkerRow';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import TextInput from '@/components/TextInput';
import { ApiEndpoints } from '@/constants/urls';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import {
  PoolRow,
  PoolTier,
  PreferredPoolsAPI,
  extractPoolsError,
} from '@/services/preferredPoolsAPI';
import styled from '@/styles';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { trackPoolMemberAdded } from '@/util/vexo';
import { workerName } from '@/util/worker';

// GRAVY-10369 — "Add worker to pool" per the Figma design (selection frame
// 38143-113423, confirmation frame 39686-200971): the pool depth section,
// Suggested/Favorites/Browse tabs over the standard worker search, and a
// per-row "Add to pool" that opens the confirm modal with an OPTIONAL reason
// (OQ-2; MemberAddService re-enforces blocks with a 422 either way).
// "Suggested workers" = the search API's highly_rated scope narrowed to the
// pool's skill. The dependability filter shows behind showTier, the same
// Worker Dependability V2 gate the staffing modal uses (GRAVY-10802).

type TabValue = 'suggested' | 'favourites' | 'browse';

const BACKEND_TAB: Record<TabValue, string> = {
  suggested: 'highly_rated',
  favourites: 'favourites',
  browse: 'all',
};

const TIER_OPTIONS: PoolTier[] = ['platinum', 'gold', 'standard', 'monitored'];

const TabList = styled('div', {
  display: 'flex',
  borderBottom: '1px solid $colors$neutralLightest',
  width: '100%',
});

const TabButton = styled('button', {
  appearance: 'none',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  cursor: 'pointer',
  padding: '10px 16px',
  fontSize: '14px',
  fontWeight: 600,
  color: '$colors$neutralLight',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  variants: {
    active: {
      true: {
        color: '$colors$themeDefault',
        borderBottomColor: '$colors$themeDefault',
      },
    },
  },
});

// The Suggested tab's count chip (design #38157:113753).
const CountBadge = styled('span', {
  alignItems: 'center',
  border: '1px solid #A7DAA1',
  borderRadius: '8px',
  color: '#44A735',
  display: 'inline-flex',
  fontSize: '12px',
  fontWeight: 700,
  justifyContent: 'center',
  minWidth: '20px',
  height: '20px',
  padding: '0 4px',
});

const TierSelect = styled('select', {
  border: '1px solid $colors$neutralLightest',
  borderRadius: '8px',
  fontSize: '14px',
  height: '36px',
  padding: '0 10px',
});

const ScrollArea = styled('div', {
  maxHeight: '420px',
  overflowY: 'auto',
  width: '100%',
});

const EmptyState = styled('div', {
  color: '#979797',
  fontSize: '14px',
  padding: '32px 0',
  textAlign: 'center',
});

type SearchWorker = {
  id: number | string;
  avatarUrl?: string | null;
  dependabilityTier?: PoolTier | null;
  selectedSkillShift?: number | string | null;
  shiftsWith?: number | string | null;
  user?: { firstName?: string; lastName?: string } | null;
};

type Props = {
  pool: PoolRow;
  customerId: string;
  onClose: () => void;
  onSuccess: () => void;
  // Same gate as the staffing modal's FilterByPopover (GRAVY-10802).
  showTier?: boolean;
  // GRAVY-10799: the client's market tax type. Candidates follow the same
  // market rule as the Staff Shift modal, so a worker who could never be
  // staffed for this client is not offered for the pool either.
  jobTaxType?: string | null;
};

const AddWorkerModal = ({
  pool,
  customerId,
  onClose,
  onSuccess,
  showTier = false,
  jobTaxType = null,
}: Props) => {
  const [tab, setTab] = useState<TabValue>('suggested');
  const [search, setSearch] = useState('');
  const debSearch = useDebouncedValue(search, 400);
  const [tierFilter, setTierFilter] = useState('');
  const [workers, setWorkers] = useState<SearchWorker[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const [suggestedCount, setSuggestedCount] = useState<number | null>(null);

  const [confirmWorker, setConfirmWorker] = useState<SearchWorker | null>(null);
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Array<number | string>>([]);

  // Monotonic fetch sequence, same rule as PreferredStaffingModal: only the
  // LATEST request may touch the list.
  const fetchSeq = useRef(0);

  useEffect(() => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    (async () => {
      try {
        const res = await axiosRubyClient.get(ApiEndpoints.GET_WORKERS_LIST, {
          params: {
            skillId: Number(pool.skill.id),
            customerId: Number(customerId),
            page: 1,
            perPage: 25,
            search: debSearch,
            tab: BACKEND_TAB[tab],
            workerType: 'gig',
            taxType: jobTaxType || 'all',
            tiers: tierFilter || null,
          },
        });
        if (seq !== fetchSeq.current) return;
        setListError(false);
        setWorkers(res?.data?.data || []);
      } catch {
        if (seq !== fetchSeq.current) return;
        setListError(true);
        setWorkers([]);
      }
      if (seq === fetchSeq.current) setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, debSearch, tierFilter, jobTaxType]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosRubyClient.get(ApiEndpoints.GET_WORKERS_COUNT, {
          params: {
            skillId: Number(pool.skill.id),
            customerId: Number(customerId),
          },
        });
        if (!cancelled) setSuggestedCount(res?.data?.data?.highlyRated ?? null);
      } catch {
        // The badge is informational; the tab works without it.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openConfirm = (worker: SearchWorker) => {
    setAddError(null);
    setReason('');
    setConfirmWorker(worker);
  };

  const confirmAdd = async () => {
    if (!confirmWorker) return;
    setAdding(true);
    setAddError(null);
    try {
      await PreferredPoolsAPI.addMember(pool.id, {
        worker_id: Number(confirmWorker.id),
        reason: reason.trim() || undefined,
      });
      trackPoolMemberAdded({
        pool_id: pool.id,
        worker_id: Number(confirmWorker.id),
        source: 'manual',
        reason_given: reason.trim().length > 0,
        entry_point: 'add_modal',
      });
      setAddedIds((prev) => [...prev, confirmWorker.id]);
      setConfirmWorker(null);
      onSuccess();
    } catch (err: unknown) {
      setAddError(extractPoolsError(err, 'Failed to add worker to pool'));
    } finally {
      setAdding(false);
    }
  };

  const skillName = pool.skill.name ?? '';

  return (
    <>
      <Modal
        disableClickout
        size="md"
        title="Add worker to pool"
        onRequestClose={onClose}
      >
        <Card.Section>
          <div style={{ marginBottom: '16px' }}>
            <PoolDepthCard pool={pool} />
          </div>
          <TabList role="tablist">
            {(
              [
                ['suggested', 'Suggested workers'],
                ['favourites', 'Favorites'],
                ['browse', 'Browse all'],
              ] as Array<[TabValue, string]>
            ).map(([value, label]) => (
              <TabButton
                key={value}
                active={tab === value}
                aria-selected={tab === value}
                role="tab"
                type="button"
                onClick={() => setTab(value)}
              >
                {label}
                {value === 'suggested' && suggestedCount != null && (
                  <CountBadge>{suggestedCount}</CountBadge>
                )}
              </TabButton>
            ))}
          </TabList>
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: '16px',
              justifyContent: 'space-between',
              margin: '12px 0',
              width: '100%',
            }}
          >
            <div style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                style={{
                  color: '#979797',
                  height: 14,
                  left: 12,
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 14,
                  zIndex: 3,
                }}
              />
              <TextInput
                css={{ paddingLeft: '34px', width: '100%' }}
                placeholder="Search GravyWorker"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {showTier && (
              <TierSelect
                aria-label="Filter by dependability"
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
              >
                <option value="">Filter by dependability</option>
                {TIER_OPTIONS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </option>
                ))}
              </TierSelect>
            )}
          </div>
          <ScrollArea>
            {workers.map((worker) => {
              const added = addedIds.includes(worker.id);
              return (
                <WorkerRow
                  key={worker.id}
                  actions={
                    added ? (
                      <Button
                        a11yLabel="Added"
                        disabled
                        label="Added"
                        type="button"
                        onClick={() => undefined}
                      />
                    ) : (
                      <Button
                        a11yLabel="Add to pool"
                        appearance="outline"
                        iconLeft={faPlus}
                        label="Add to pool"
                        type="button"
                        onClick={() => openConfirm(worker)}
                      />
                    )
                  }
                  countSource="search"
                  rookie={Number(worker.shiftsWith) === 0}
                  shiftsInSkillTotal={Number(worker.selectedSkillShift) || 0}
                  shiftsWithClient={Number(worker.shiftsWith) || 0}
                  skillName={skillName}
                  tier={worker.dependabilityTier ?? null}
                  worker={{
                    id: Number(worker.id),
                    name: workerName(worker),
                    avatar_url: worker.avatarUrl ?? null,
                  }}
                />
              );
            })}
            {!loading && listError && (
              <EmptyState>We couldn&apos;t load workers.</EmptyState>
            )}
            {!loading && !listError && workers.length === 0 && (
              <EmptyState>
                {debSearch
                  ? 'No workers match your search.'
                  : 'No workers available.'}
              </EmptyState>
            )}
          </ScrollArea>
        </Card.Section>
      </Modal>
      {confirmWorker && (
        <Modal
          disableClickout
          size="sm"
          title="Add to worker pool"
          onRequestClose={() => setConfirmWorker(null)}
        >
          <Card.Section>
            {addError && (
              <Alert
                description={addError}
                icon={faExclamationTriangle}
                status="danger"
                title="Error"
              />
            )}
            <p style={{ fontSize: '16px', marginBottom: '16px' }}>
              {`Add ${workerName(confirmWorker)} to client's `}
              <b>{skillName.toLowerCase()}</b>
              {' pool?'}
            </p>
            <TextArea
              placeholder="Add reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Card.Section>
          <Card.Section>
            <Stack justify="end">
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                type="button"
                onClick={() => setConfirmWorker(null)}
              />
              <Button
                a11yLabel="Add to pool"
                isLoading={adding}
                label="Add to pool"
                type="button"
                onClick={confirmAdd}
              />
            </Stack>
          </Card.Section>
        </Modal>
      )}
    </>
  );
};

export default AddWorkerModal;
